import { useMemo, useState } from 'react';
import { Modal, Form, Select, Radio, Space, Statistic, Button, Typography, App } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useLanguages } from '@/hooks/useLanguages';
import { useProviders, useStartTranslate } from '@/hooks/useTranslateBatch';
import { getAiTasks } from '@/api/aiTasks';
import { estimateRunCost, estimateUiTokens, estimateTopicTokens } from '@/lib/cost-estimate';
import { useTranslateJobsStore } from '@/stores/translateJobsStore';
import { AiTranslateProgress } from './AiTranslateProgress';

interface Props {
  open: boolean;
  defaultTargetLang?: string;
  defaultSourceLang?: string;
  defaultScope?: Scope;
  /** When set, translation is scoped to this single topic (topics scope). */
  topicId?: string;
  /** Called when a translate job reaches a terminal state, so the editor can reload. */
  onTranslated?: () => void;
  onClose: () => void;
}

type Scope = 'ui_strings' | 'topics' | 'both';
type OverwritePolicy = 'missing-only' | 'all';

export function AiTranslateModal({
  open,
  defaultTargetLang,
  defaultSourceLang = 'en',
  defaultScope = 'ui_strings',
  topicId,
  onTranslated,
  onClose,
}: Props) {
  const { message } = App.useApp();
  const langsQ = useLanguages();
  const providersQ = useProviders();
  const startMut = useStartTranslate();
  const trackJob = useTranslateJobsStore((s) => s.track);

  // Track the last-seen defaultTargetLang prop so external changes are picked
  // up without using setState-in-effect (lint rule react-hooks/set-state-in-effect).
  const [lastDefaultTarget, setLastDefaultTarget] = useState<string | undefined>(defaultTargetLang);
  const [targetOverride, setTargetOverride] = useState<string>(defaultTargetLang ?? '');
  if (defaultTargetLang !== lastDefaultTarget) {
    setLastDefaultTarget(defaultTargetLang);
    setTargetOverride(defaultTargetLang ?? '');
  }
  const target = targetOverride;
  const setTarget = setTargetOverride;

  const [source, setSource] = useState<string>(defaultSourceLang);
  const [scope, setScope] = useState<Scope>(defaultScope);
  const [overwrite, setOverwrite] = useState<OverwritePolicy>('missing-only');
  const [jobId, setJobId] = useState<string | null>(null);
  const [estUiCount] = useState(80); // user-overridable rough estimate
  const [estTopicCount] = useState(200);

  // Model is configured server-side (AI Settings → Function → Model). Resolve the
  // translate model here only to price the run — the request omits `model`.
  const tasksQ = useQuery({ queryKey: ['ai-tasks'], queryFn: getAiTasks });
  const model =
    tasksQ.data?.tasks.find((t) => t.key === 'translate')?.model ??
    tasksQ.data?.default_model ??
    undefined;

  const provider = providersQ.data?.find((p) => p.model === model);

  const estCost = useMemo(() => {
    if (!provider) return 0;
    let inT = 0;
    let outT = 0;
    if (scope === 'ui_strings' || scope === 'both') {
      const u = estimateUiTokens(estUiCount);
      inT += u.input;
      outT += u.output;
    }
    if (scope === 'topics' || scope === 'both') {
      const t = estimateTopicTokens(estTopicCount);
      inT += t.input;
      outT += t.output;
    }
    return estimateRunCost({
      inputTokens: inT,
      outputTokens: outT,
      inputPrice: provider.input_price,
      outputPrice: provider.output_price,
    });
  }, [provider, scope, estUiCount, estTopicCount]);

  async function start() {
    if (!target) {
      message.error('Pick a target language');
      return;
    }
    if (target === source) {
      message.error('Source and target must differ');
      return;
    }
    try {
      const r = await startMut.mutateAsync({
        target_lang: target,
        source_lang: source,
        // In a topic context we only ever translate this topic's content — never
        // ui_strings (English UI strings live in the app bundle → no source rows).
        scope: topicId ? 'topics' : scope,
        overwrite_policy: overwrite,
        topic_ids: topicId ? [topicId] : undefined,
      });
      trackJob(r.job_id);
      setJobId(r.job_id);
    } catch (e) {
      message.error((e as Error).message);
    }
  }

  function reset() {
    setJobId(null);
    onClose();
  }

  return (
    <Modal
      open={open}
      title="AI Translate"
      onCancel={reset}
      footer={null}
      width={720}
      destroyOnHidden
    >
      {jobId ? (
        <>
          <AiTranslateProgress
            jobId={jobId}
            onTerminal={(s, items) => {
              if (s === 'success' && items === 0)
                message.info('Already translated — nothing new. Choose "Overwrite all" to re-translate.');
              else if (s === 'success') message.success(`Translation complete — ${items} item(s)`);
              if (s === 'partial') message.warning('Translation finished with some failures');
              if (s === 'failed') message.error('Translation failed');
              // Any non-failed terminal state means the DB may now hold fresh
              // translations (or already did) — let the editor reload them.
              if (s !== 'failed') onTranslated?.();
            }}
          />
          <div style={{ marginTop: 16, textAlign: 'right' }}>
            <Space>
              <Button onClick={reset}>Close (job continues in background)</Button>
              <Button type="primary" onClick={() => setJobId(null)}>
                New translation
              </Button>
            </Space>
          </div>
        </>
      ) : (
        <Form layout="vertical">
          {topicId ? (
            <Form.Item label="Translate this topic">
              <Typography.Text strong>English → {target}</Typography.Text>
            </Form.Item>
          ) : (
            <>
              <Form.Item label="Target language">
                <Select
                  value={target}
                  onChange={setTarget}
                  options={(langsQ.data ?? [])
                    .filter((l) => !l.is_bundled)
                    .map((l) => ({ value: l.code, label: `${l.code} — ${l.name_native}` }))}
                  placeholder="Select language to translate INTO"
                />
              </Form.Item>
              <Form.Item label="Source language">
                <Select
                  value={source}
                  onChange={setSource}
                  options={(langsQ.data ?? []).map((l) => ({
                    value: l.code,
                    label: `${l.code} — ${l.name_native}`,
                  }))}
                />
              </Form.Item>
              <Form.Item label="Scope">
                <Radio.Group value={scope} onChange={(e) => setScope(e.target.value)}>
                  <Radio value="ui_strings">UI strings</Radio>
                  <Radio value="topics">Topic content</Radio>
                  <Radio value="both">Both</Radio>
                </Radio.Group>
              </Form.Item>
            </>
          )}
          <Form.Item label="Overwrite policy">
            <Radio.Group value={overwrite} onChange={(e) => setOverwrite(e.target.value)}>
              <Radio value="missing-only">Missing only (preserve existing translations)</Radio>
              <Radio value="all">Overwrite all</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item label="Model">
            <Typography.Text strong>{model ?? '— none configured —'}</Typography.Text>
            <Typography.Text type="secondary"> · change in AI Settings → Function → Model</Typography.Text>
          </Form.Item>
          {!topicId && (
            <Space size="large" style={{ marginTop: 4 }}>
              <Statistic title="≈ Estimated cost" value={`$${estCost.toFixed(4)}`} />
              <Statistic title="UI keys est." value={estUiCount} />
              <Statistic title="Topics est." value={estTopicCount} />
            </Space>
          )}
          <div style={{ marginTop: 16, textAlign: 'right' }}>
            <Space>
              <Button onClick={reset}>Cancel</Button>
              <Button type="primary" onClick={start} loading={startMut.isPending}>
                Start translation
              </Button>
            </Space>
          </div>
        </Form>
      )}
    </Modal>
  );
}
